import * as React from 'react';
import { useState, useEffect, useCallback } from 'react';
import { Redirect } from 'react-router-dom';
import { Flashbar, FlashbarProps, SpaceBetween } from '@amzn/awsui-components-react-v3';

import { approveRequest, cancelRequest, denyRequest, getRequest } from 'src/api/permissions';
import { Page } from 'src/routes';
import {
  DenyOrCancelModal,
  ResourceBasedRequestInformation,
  TagBasedRequestInformation,
} from 'src/components/workspaces/requests/utils';
import {
  ALL_TABLES_RES_NAME,
  APPROVALS_STATUS_APPROVED,
  APPROVALS_STATUS_APPROVED_WITH_CONDITION,
  APPROVALS_STATUS_CANCELLED,
  APPROVALS_STATUS_REJECTED,
  TABLE_CONTENT_TYPE,
} from 'src/commons/constants';
import { extractAccessManagementInfoFromRequest } from 'src/components/requests/utils';
import { activeGroupOrWorkspaceId } from 'src/components/dataadvisory/constants';

import { PageHeader } from 'src/components/common/PageHeader';
import { GetRequestResult, GladstoneRequestItem } from 'aws-sdk/clients/awsdatalakegladstonelambda';

const transformData = (item: GladstoneRequestItem) => {
  let transform = {};
  transform['requestId'] = item.requestId;
  transform['principal'] = item.dataPermissionRequest.dataLakePrincipal;
  transform['status'] = item.status;
  transform['statusReason'] = item.statusReason;
  transform['owner'] = item.ownerRequestedTo;
  transform['ownerRequestedBy'] = item.ownerRequestedBy;
  transform['requestedBy'] = item.requestedBy;
  transform['decidedBy'] = item.decidedBy;
  transform['timeOfRequest'] = item.timeOfRequest;
  transform['timeOfDecision'] = item.timeOfDecision;
  transform['useCase'] = item.useCase;
  transform['dataConsumerType'] = item.dataConsumerType;
  transform['reasonOfAction'] = item.reasonOfAction;
  transform['approvedPermissionId'] = item.approvedPermissionId;
  transform = extractAccessManagementInfoFromRequest(item, transform);
  // Support for legacy LF requests
  if (!!item.lakeFormationRequest) {
    transform['permissions'] = JSON.parse(item.lakeFormationRequest.PermissionsString);
    transform['grantablePermissions'] = JSON.parse(item.lakeFormationRequest.PermissionsWithGrantOptionString);
    transform['resource'] = JSON.parse(item.lakeFormationRequest.ResourceString);
  } else {
    transform['permissions'] = item.dataPermissionRequest.permissions;
    transform['grantablePermissions'] = item.dataPermissionRequest.permissionsWithGrantOption;
    transform['resource'] = item.dataPermissionRequest.resource;
  }

  if (transform['resource'].lFTagPolicy) {
    transform['resourceTag'] = '';
    transform['resource'].lFTagPolicy.expression.forEach((expression) => {
      var resourceTag = '';
      var key = expression.tagKey;
      for (var i = 0; i < expression.tagValues.length; i++) {
        resourceTag += key + '.' + expression.tagValues[i] + ' ';
      }
      transform['resourceTag'] += resourceTag;
    });
  } else if (transform['resource'].redshiftTagPolicy) {
    transform['resourceTag'] = '';
    let policy = transform['resource'].redshiftTagPolicy;
    transform['resourceTag'] = policy.tagKey + '.' + policy.tagValue;
  } else if (transform['resource'].table) {
    transform['type'] = 'table';
    transform['catalogId'] = transform['resource'].table.catalogId;
    transform['databaseName'] = transform['resource'].table.databaseName;
    transform['resource'].table.tableWildcard
      ? (transform['tableName'] = ALL_TABLES_RES_NAME)
      : (transform['tableName'] = transform['resource'].table.name);
    transform['columnNames'] = 'All Columns';
  } else if (transform['resource'].dataCellsFilter) {
    transform['catalogId'] = transform['resource'].dataCellsFilter.tableCatalogId;
    transform['clusterId'] = transform['resource'].dataCellsFilter.clusterId;
    transform['databaseName'] = transform['resource'].dataCellsFilter.databaseName;
    transform['schemaName'] = transform['resource'].dataCellsFilter.schemaName;
    transform['tableName'] = transform['resource'].dataCellsFilter.tableName;
    transform['fgapName'] = transform['resource'].dataCellsFilter.name;
  } else if (transform['resource'].dataZoneResource) {
    transform['catalogId'] = transform['resource'].dataZoneResource.awsAccountId;
    transform['clusterId'] = transform['resource'].dataZoneResource.clusterId;
    transform['redshiftWorkgroupName'] = transform['resource'].dataZoneResource.redshiftWorkgroupName;
    transform['databaseName'] = transform['resource'].dataZoneResource.databaseName;
    transform['schemaName'] = transform['resource'].dataZoneResource.schemaName;
    transform['tableName'] = transform['resource'].dataZoneResource.tableName;
  } else {
    transform['type'] = 'column';
    transform['catalogId'] = transform['resource'].tableWithColumns.catalogId;
    transform['databaseName'] = transform['resource'].tableWithColumns.databaseName;
    transform['tableName'] = transform['resource'].tableWithColumns.name;
    if (transform['resource'].tableWithColumns.columnNames) {
      transform['columnNames'] = transform['resource'].tableWithColumns.columnNames.join(', ');
      transform['columnWildCard'] = 'Include';
    } else {
      transform['columnNames'] = transform['resource'].tableWithColumns.columnWildcard.excludedColumnNames.join(', ');
      transform['columnWildCard'] = 'Exclude';
    }
  }
  return transform;
};

export interface PermissionRequestDetailsProps {
  setContentType: any;
  match: any;
  activeGroup: string;
  activeWorkspace?: any;
}

export const PermissionRequestDetails = (props: PermissionRequestDetailsProps) => {
  const [notifications, setNotifications] = useState<FlashbarProps.MessageDefinition[]>([]);
  const [action, setAction] = useState<string | undefined>(undefined);
  const [actionLoading, setActionLoading] = useState(false);
  const [buttonLoading, setButtonLoading] = useState(false);
  const [redirect, setRedirect] = useState<string | undefined>(undefined);
  const [modalVisible, setModalVisible] = useState(false);
  const [isInvalid, setIsInvalid] = useState(false);
  const [request, setRequest] = useState<any>('');
  const [textAreaPlaceholder, setTextAreaPlaceholder] = useState<string | undefined>(undefined);
  const [reasonOfAction, setReasonOfAction] = useState<any>(undefined);

  const closeModal = useCallback(() => {
    setModalVisible(false);
    setAction(undefined);
    setButtonLoading(false);
  }, []);

  const handleRefresh = useCallback(async () => {
    closeModal();
    if (!props.activeGroup) return;
    let requestResult: GetRequestResult;
    try {
      if (props.match.params.id) {
        requestResult = await getRequest({
          requestId: props.match.params.id,
        });
        setRequest(transformData(requestResult.requestItem));
      } else {
        setNotifications([
          {
            type: 'error' as FlashbarProps.Type,
            content: 'No request found for given ID.',
            dismissible: true,
            onDismiss: () => setNotifications([]),
          },
        ]);
      }
    } catch (err) {
      console.log(err);
      setNotifications([
        {
          type: 'error' as FlashbarProps.Type,
          content: `Failed to load request detail`,
          dismissible: true,
          onDismiss: () => setNotifications([]),
        },
      ]);
    }
  }, [props.activeGroup, props.match.params.id, closeModal]);

  const openModal = (clickedAction: string) => {
    setModalVisible(true);
    setAction(clickedAction);
  };

  const successMessage = (message: string, actionType: string) => {
    if (actionType === 'approved') {
      setNotifications([
        {
          type: 'success',
          content: `Data permission request '${message}' was '${actionType}'`,
          dismissible: true,
          buttonText: 'View datasets',
          onButtonClick: () => setRedirect(Page.WORKSPACE_ALL_PERMISSIONS),
          onDismiss: () => setNotifications([]),
        },
      ]);
    } else {
      setNotifications([
        {
          type: 'success',
          content: `Data permission request '${message}' was '${actionType}'`,
          dismissible: true,
          onDismiss: () => setNotifications([]),
        },
      ]);
    }
  };

  const errorMessage = (message: string, actionType: string) => {
    setNotifications([
      {
        type: 'error',
        content: `There was an error '${actionType}' your data access request: '${message}'`,
        dismissible: true,
        onDismiss: () => setNotifications([]),
      },
    ]);
  };

  const submitAction = async (actionType: string) => {
    if (actionType === 'cancel') {
      if (!reasonOfAction) {
        setIsInvalid(true);
        return;
      }
      setButtonLoading(true);
      try {
        await cancelRequest({
          requestId: request['requestId'],
          reason: reasonOfAction,
        });
        successMessage(request['requestId'], 'cancelled');
        await handleRefresh();
      } catch (err) {
        console.log(err);
        errorMessage(err.message, 'canceling');
      }
    } else if (actionType === 'approve') {
      setActionLoading(true);
      try {
        await approveRequest({
          requestId: request['requestId'],
        });
        successMessage(request['requestId'], 'approved');
        await handleRefresh();
      } catch (err) {
        console.log(err);
        errorMessage(err.message, 'approving');
      }
      setActionLoading(false);
    } else if (actionType === 'deny') {
      if (!reasonOfAction) {
        setIsInvalid(true);
        return;
      }
      setButtonLoading(true);
      try {
        await denyRequest({
          requestId: request['requestId'],
          reason: reasonOfAction,
        });
        successMessage(request['requestId'], 'denied');
        await handleRefresh();
      } catch (err) {
        console.log(err);
        errorMessage(err.message, 'denying');
      }
    }
  };

  const handleAction = async (e: any) => {
    if (e.detail.id === 'cancel') {
      openModal('cancel');
    } else if (e.detail.id === 'approve') {
      await submitAction(e.detail.id);
    } else if (e.detail.id === 'deny') {
      openModal('deny');
    } else if (e.detail.id === 'view') {
      await submitAction(e.detail.id);
    }
  };

  const shouldDisableDenyButton = () => {
    if (request['status'] != 'PENDING') return true;
    return false;
  };

  const shouldDisableApproveButton = () => {
    if (request['status'] != 'PENDING') return true;
    if (!request['approvalsWorkflow']) return false;
    let anyApprovalWorkflowsArePending = false;
    request['approvalsWorkflow'].forEach((item: any) => {
      if (
        item.approvalWorkflowStatus != APPROVALS_STATUS_APPROVED &&
        item.approvalWorkflowStatus != APPROVALS_STATUS_APPROVED_WITH_CONDITION
      ) {
        anyApprovalWorkflowsArePending = true;
      }
    });
    return anyApprovalWorkflowsArePending;
  };

  const approvalWorkflowStatusOutOfSync = (desiredStatuses: string[]) => {
    let outOfSync = false;
    if (request['approvalsWorkflow']) {
      request['approvalsWorkflow'].forEach((item: any) => {
        if (!desiredStatuses.includes(item.approvalWorkflowStatus)) {
          outOfSync = true;
        }
      });
    }
    return outOfSync;
  };

  const options = () => {
    let actionItems: any[] = [];
    if (activeGroupOrWorkspaceId(props) === request['ownerRequestedBy']) {
      actionItems = [
        {
          text: 'Cancel',
          id: 'cancel',
          disabled: request['status'] !== 'PENDING',
        },
      ];
    }
    if (activeGroupOrWorkspaceId(props) === request['owner']) {
      actionItems.push(
        {
          text: 'Approve',
          id: 'approve',
          disabled: shouldDisableApproveButton(),
        },
        {
          text: 'Deny',
          id: 'deny',
          disabled: shouldDisableDenyButton(),
        },
      );
    }
    return [
      {
        text: '',
        icon: 'refresh',
        onItemClick: handleRefresh,
      },
      {
        text: 'Actions',
        onItemClick: handleAction,
        loading: actionLoading,
        items: actionItems,
      },
    ];
  };

  useEffect(() => {
    props.setContentType(TABLE_CONTENT_TYPE);
    handleRefresh();
  }, []);

  useEffect(() => {
    handleRefresh();
  }, [handleRefresh]);

  if (redirect) {
    return <Redirect push to={redirect} />;
  }

  return (
    <SpaceBetween size='xs'>
      <Flashbar items={notifications} />
      <DenyOrCancelModal
        modalVisible={modalVisible}
        action={action}
        closeModal={closeModal}
        buttonLoading={buttonLoading}
        submitAction={submitAction}
        approvalWorkflowStatusOutOfSync={approvalWorkflowStatusOutOfSync([
          APPROVALS_STATUS_REJECTED,
          APPROVALS_STATUS_CANCELLED,
        ])}
        textAreaPlaceholder={textAreaPlaceholder}
        reasonOfAction={reasonOfAction}
        setReasonOfAction={setReasonOfAction}
      />
      <PageHeader buttons={options()} header={`Request ID: ${props.match.params.id}`} />
      {!request['resourceTag'] && <ResourceBasedRequestInformation request={request} />}
      {request['resourceTag'] && <TagBasedRequestInformation request={request} />}
    </SpaceBetween>
  );
};
